// src/pages/Wallet/hooks/usePortfolioCache.js
import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { WalletContext } from '../../../contexts/WalletContext';
import { NetworkContext } from '../../../contexts/NetworkContext';

// Minimum time between cache updates (5 minutes in milliseconds)
const CACHE_INTERVAL = 5 * 60 * 1000;

// Delay before caching to ensure all token data is loaded (10 seconds)
const CACHE_DELAY = 10 * 1000;

// Increasing maximum entries to store per chain/wallet
// For 30 days: 30 days * 24 hours * 12 entries/hour = 8640 entries
const MAX_ENTRIES_PER_CHAIN = 8640; // Increased from 500 to store 30 days of data

/**
 * Hook to manage portfolio value caching
 * Handles storing and retrieving portfolio value history data
 */
export const usePortfolioCache = (isDataLoading = false) => {
  const { currentWallet } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  const [lastCacheTime, setLastCacheTime] = useState(0);
  const cacheTimeoutRef = useRef(null);
  
  // Load cached data from localStorage
  const getPortfolioData = useCallback(() => {
    try {
      const savedData = localStorage.getItem('portfolioData');
      return savedData ? JSON.parse(savedData) : {};
    } catch (e) {
      console.error('Error loading portfolio data from localStorage:', e);
      return {};
    }
  }, []);
  
  // Clear any existing cache timeout
  const clearCacheTimeout = useCallback(() => {
    if (cacheTimeoutRef.current) {
      clearTimeout(cacheTimeoutRef.current);
      cacheTimeoutRef.current = null;
    }
  }, []);
  
  // Save portfolio value to cache after delay
  const cachePortfolioValue = useCallback((value) => {
    if (!currentWallet || !currentChain || value <= 0) return;
    
    const now = Date.now();
    // Only cache if at least 5 minutes have passed since last cache
    if (now - lastCacheTime < CACHE_INTERVAL) return;
    
    // Clear any existing timeout
    clearCacheTimeout();
    
    // Set a timeout to wait for all token data to be loaded
    cacheTimeoutRef.current = setTimeout(() => {
      try {
        const portfolioData = getPortfolioData();
        const walletAddress = currentWallet.address.toLowerCase();
        const chainKey = currentChain.key;
        
        // Initialize structure if needed
        if (!portfolioData[walletAddress]) {
          portfolioData[walletAddress] = {};
        }
        
        if (!portfolioData[walletAddress][chainKey]) {
          portfolioData[walletAddress][chainKey] = [];
        }
        
        // Add new data point
        portfolioData[walletAddress][chainKey].push({
          timestamp: now,
          value: value
        });
        
        // Trim old data if we have too many entries
        if (portfolioData[walletAddress][chainKey].length > MAX_ENTRIES_PER_CHAIN) {
          portfolioData[walletAddress][chainKey] = portfolioData[walletAddress][chainKey]
            .slice(-MAX_ENTRIES_PER_CHAIN);
        }
        
        // Save to localStorage
        localStorage.setItem('portfolioData', JSON.stringify(portfolioData));
        
        // Update last cache time
        setLastCacheTime(now);
        
        console.log(`Portfolio value cached: $${value.toFixed(2)} for ${chainKey}`);
      } catch (e) {
        console.error('Error saving portfolio data to localStorage:', e);
      }
    }, CACHE_DELAY);
    
  }, [currentWallet, currentChain, lastCacheTime, getPortfolioData, clearCacheTimeout]);
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      clearCacheTimeout();
    };
  }, [clearCacheTimeout]);
  
  // Get cached portfolio history for the current wallet and chain
  const getCurrentWalletHistory = useCallback((timeframe = '24h') => {
    if (!currentWallet || !currentChain) return [];
    
    const portfolioData = getPortfolioData();
    const walletAddress = currentWallet.address.toLowerCase();
    const chainKey = currentChain.key;
    
    // If no data exists for this wallet/chain, return empty array
    if (!portfolioData[walletAddress] || !portfolioData[walletAddress][chainKey]) {
      return [];
    }
    
    const now = Date.now();
    let cutoffTime;
    
    // Determine cutoff time based on timeframe
    switch (timeframe) {
      case '24h':
        cutoffTime = now - (24 * 60 * 60 * 1000);
        break;
      case '1w':
        cutoffTime = now - (7 * 24 * 60 * 60 * 1000);
        break;
      case '1m':
        cutoffTime = now - (30 * 24 * 60 * 60 * 1000);
        break;
      default:
        cutoffTime = now - (24 * 60 * 60 * 1000);
    }
    
    // Filter by timeframe and sort by timestamp
    const filteredData = portfolioData[walletAddress][chainKey]
      .filter(item => item.timestamp >= cutoffTime)
      .sort((a, b) => a.timestamp - b.timestamp);
    
    // Ensure we have at least two data points for charting
    if (filteredData.length === 1) {
      // Create a fake older data point to allow drawing a chart
      // This is using the same value as the existing point
      // This will result in a flat line which is better than no chart
      const oldDataPoint = {
        timestamp: cutoffTime,
        value: filteredData[0].value
      };
      
      return [oldDataPoint, ...filteredData];
    }
    
    return filteredData;
    
  }, [currentWallet, currentChain, getPortfolioData]);
  
  // Get all portfolio data for the application
  const getAllPortfolioData = useCallback(() => {
    return getPortfolioData();
  }, [getPortfolioData]);
  
  // Clear all portfolio data
  const clearAllPortfolioData = useCallback(() => {
    try {
      localStorage.removeItem('portfolioData');
      console.log('All portfolio data cleared');
      return true;
    } catch (e) {
      console.error('Error clearing portfolio data:', e);
      return false;
    }
  }, []);
  
  return {
    cachePortfolioValue,
    getCurrentWalletHistory,
    getAllPortfolioData,
    clearAllPortfolioData
  };
};